import React, { useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { HouseICalendarUrlsInputDto } from '../../../../../../../../../lib/graphql'
import { Button } from '../../../../../../../../../shadcn/components/ui/button'
import { ArrayElement } from '../../../../../../../../../types'
import { Icon, SimpleModal, Spinner } from '../../../../../../../../atoms'
import { ControlledTextField } from '../../../../../../../../forms'
import { BigCalendarProps } from '../../../BigCalendar'

const URL_REGEX =
  /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/

type HouseICalendarsUrlFormData = {
  urls: { url: ArrayElement<HouseICalendarUrlsInputDto['urls']> }[]
}

export type BigCalendarHeaderICalendarImportProps = {
  house: Pick<BigCalendarProps['house'], 'iCalendars' | 'iCalURL'>
  onUpdateICalendarUrls: {
    mutate: (formData: HouseICalendarUrlsInputDto) => void
    isLoading: boolean
  }
}
export const BigCalendarHeaderICalendarImport = ({
  house: { iCalendars },
  onUpdateICalendarUrls: { isLoading, mutate },
}: BigCalendarHeaderICalendarImportProps) => {
  const intl = useIntl()
  const [isOpen, setIsOpen] = useState(false)

  const handleOnOpen = () => {
    setIsOpen(true)
  }

  const handleOnClose = () => {
    setIsOpen(false)

    reset()
  }

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm<HouseICalendarsUrlFormData>({
    defaultValues: { urls: iCalendars.urls.map((url) => ({ url })) },
  })

  const { fields, append, remove } = useFieldArray<HouseICalendarsUrlFormData>({
    control,
    name: 'urls',
  })

  const onSubmit = (formData: HouseICalendarsUrlFormData) => {
    const urls = formData.urls.map(({ url }) => url)

    mutate({ urls })

    reset(formData)

    setIsOpen(false)
  }

  return (
    <>
      <Button onClick={handleOnOpen} disabled={isLoading}>
        <div className='flex items-center gap-2'>
          <span>
            <FormattedMessage
              defaultMessage='Importer iCal'
              id='ui.BigCalendarHeaderICalendarImport.importICal'
            />
          </span>
          {isLoading ? (
            <Spinner height={20} width={20} />
          ) : (
            <Icon name='download' size={22} />
          )}
        </div>
      </Button>

      <SimpleModal
        hasError={false}
        isLoading={false}
        name='big-calendar-icalendar-import-modal'
        isOpen={isOpen}
        onClose={handleOnClose}
        style={{ height: '70vh', width: '50vw' }}
        content={{
          body: (
            <div className='flex h-full flex-col gap-8 overflow-scroll p-8'>
              <div className='text-grey-700'>
                <FormattedMessage
                  id='ui.BigCalendarHeaderICalendarImport.SyncCalendarsWithNaboo'
                  defaultMessage="Votre logement est déjà disponible sur d'autres plateformes ? {br}Synchronisez vos calendriers avec Naboo afin d'optimiser vos réservations."
                  values={{
                    br: <br />,
                  }}
                />
              </div>

              <div className='flex h-full flex-col gap-4'>
                {fields.length > 0 ? (
                  fields.map(({ id }, index) => (
                    <div key={id} className='flex items-center gap-2'>
                      <ControlledTextField
                        control={control}
                        name={`urls.[${index}].url`}
                        placeholder={intl.formatMessage({
                          defaultMessage: "Collez ici l'adresse iCal",
                          id: 'ui.BigCalendarHeaderICalendarImport.pasteHereTheICalAddress',
                        })}
                        fullWidth
                        style={{ margin: 0, width: '300px' }}
                        rules={{
                          required: intl.formatMessage({
                            defaultMessage: 'Lien ical requis',
                            id: 'ui.BigCalendarHeaderICalendarImport.iCalLinkRequired',
                          }),
                          pattern: {
                            value: URL_REGEX,
                            message: intl.formatMessage({
                              defaultMessage: 'Vérifiez votre lien ical',
                              id: 'ui.BigCalendarHeaderICalendarImport.checkYourICalLink',
                            }),
                          },
                        }}
                      />

                      <button onClick={() => remove(index)}>
                        <Icon name='cross' size={16} color='var(--red-600)' />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className='flex h-full w-full flex-col items-center justify-center gap-4 text-sm text-grey-600'>
                    <Icon
                      name='calendar-disabled'
                      size={62}
                      color='var(--grey-500)'
                    />
                    <FormattedMessage
                      defaultMessage='Aucune adresse iCal associée'
                      id='ui.BigCalendarHeaderICalendarImport.NoICalAddressAssociated'
                    />
                  </div>
                )}
              </div>

              <button
                className='end flex items-center gap-2 justify-self-end text-sm'
                onClick={() => append({ url: '' })}
              >
                <Icon name='add' size={16} />
                <div>
                  <FormattedMessage
                    defaultMessage='Ajouter une adresse iCal'
                    id='ui.BigCalendarHeaderICalendarImport.AddICalAddress'
                  />
                </div>
              </button>
            </div>
          ),
          footer: (
            <div className='flex justify-between'>
              <Button
                onClick={() =>
                  window.open(
                    'https://www.airbnb.fr/help/article/99/synchronisation-de-votre-calendrier-airbnb-avec-dautres-calendriers#section-heading-5-0',
                    '_blank',
                  )
                }
              >
                <FormattedMessage
                  defaultMessage='Comment trouver mon ICal ?'
                  id='ui.BigCalendarHeaderICalendarImport.HowToFindMyICal'
                />
              </Button>

              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid || !isDirty || isLoading}
                className='min-w-[120px]'
              >
                {isLoading ? (
                  <Spinner height={20} width={20} />
                ) : (
                  intl.formatMessage({
                    defaultMessage: 'Enregistrer',
                    id: 'ui.BigCalendarHeaderICalendarImport.Save',
                  })
                )}
              </Button>
            </div>
          ),
          header: (
            <div className='font-bold'>
              <FormattedMessage
                defaultMessage='Synchronisez vos calendriers externes avec Naboo'
                id='ui.BigCalendarHeaderICalendarImport.SyncExternalCalendarsWithNaboo'
              />
            </div>
          ),
        }}
      />
    </>
  )
}
